import isWithinInterval from 'date-fns/isWithinInterval';
import { List, Map, Set } from 'immutable';
import { vega } from 'vega-embed';

import mergeSortedLists from 'shared/lib/mergeSortedLists';
import {
  breakString,
  getElementsAround,
  interpolate,
  isBlank,
  isNullOrUndefined,
  isNumber,
  isPresent,
} from 'shared/lib/utils';

import { AXIS_FONT_SIZE } from '@/lib/config';

import * as Annotation from '@/lib/data/annotation';
import { DOMAIN_COLOR, canonicalizeLayerName } from '@/lib/data/vega';

export const Y_FIELD_MARK_LAYER_PREFIX = 'y-field-mark';
export const Y_FIELD_LABEL_LAYER_PREFIX = 'y-field-label';
export const ANNOTATION_POINTERS_LAYER_PREFIX = 'annotation-pointers';
export const ANNOTATION_POINTER_TARGETS_LAYER_PREFIX =
  'annotation-pointer-targets';
export const ANNOTATION_TEXTS_LAYER_PREFIX = 'annotation-texts';

export const YEAR_FORMATS = new Set(['%Y']);
export const YEAR_MONTH_FORMATS = new Set(['%B %Y', '%b %Y', '%Y-%m']);
export const YEAR_MONTH_DAY_FORMATS = new Set(['%b %e, %Y', '%Y-%m-%d', '']);
export const MONTH_FORMATS = new Set(['%B', '%b']);
export const MONTH_DAY_FORMATS = new Set(['%B %e', '%b %e']);
export const HOUR_MINUTE_FORMATS = new Set(['%H:%M']);

const POINT_MARK_STROKE_WIDTH_SIZE_MULTIPLIER = 20;
const PERCENTAGE_Y_AXIS_PADDING_VERTICAL = 10;
const MINIMUM_DISTANCE_BETWEEN_Y_FIELD_LABEL_AND_MARK = 10;

export function getXAxis(chart) {
  // TODO: handle multiple datasets somehow.
  // TODO: handle multiple x axes somehow.
  return chart.getIn(['datasets', 0, 'metadata', 'xAxes', 0]);
}

export function xAxisDateRange(chart) {
  const chartXAxis = getXAxis(chart);

  if (!chartXAxis) {
    return null;
  }

  return [
    new Date(chartXAxis.get('earliest')),
    new Date(chartXAxis.get('latest')),
  ];
}

// NOTE: keep in sync with backend.
export function isDatasetCreatedFromAnnotationSet(dataset) {
  return dataset.getIn(['metadata', 'createdFrom']) === 'annotation-set';
}

export function getSelectedYAxes(chart) {
  const dataset = chart.getIn(['datasets', 0]);

  return dataset
    .getIn(['metadata', 'yAxes'])
    .filter((yAxis) => isDatasetYAxisSelected(yAxis))
    .sortBy((yAxis) => yAxesSorter(dataset, yAxis));
}

function makeInterpolateFromSurroudingsFunctionName(story) {
  return `interpolateFromSurroundings_${cleanAxisName(story.get('id'))}`;
}

export function getDatasetFieldValueOn(story, yAxis, dateString) {
  const chart = story.get('chart');
  const rows = chart.getIn(['datasets', 0, 'data']);
  const xAxis = getXAxis(chart);
  const date = new Date(dateString);

  if (
    !isWithinInterval(date, {
      start: new Date(yAxis.get('datetimeEarliest')),
      end: new Date(yAxis.get('datetimeLatest')),
    })
  ) {
    return null;
  }

  const rowWithTimestamp = rows
    .filter((row) => row.get(xAxis.get('index')) === dateString)
    .first();

  if (!rowWithTimestamp) {
    return {
      estimated: true,
      value: window[makeInterpolateFromSurroudingsFunctionName(story)](
        date.getTime(),
        cleanAxisName(yAxis.get('name'))
      ),
    };
  }

  const yFieldValueAtTimestamp = rowWithTimestamp.get(yAxis.get('index'));

  if (isNumber(yFieldValueAtTimestamp)) {
    return { estimated: false, value: yFieldValueAtTimestamp };
  }

  return null;
}

export function fieldVariableAxisFormat(chart, field) {
  const leftAxisVariables = chart
    .getIn(['dualAxisChartProperties', 'leftAxisVariables'])
    .toSet();
  const rightAxisVariables = chart
    .getIn(['dualAxisChartProperties', 'rightAxisVariables'])
    .toSet();

  const fieldVariable = field.get('variable');

  const fieldLeftOrRight = leftAxisVariables.has(fieldVariable)
    ? 'Left'
    : rightAxisVariables.has(fieldVariable)
    ? 'Right'
    : null;

  if (fieldLeftOrRight === null) {
    return null;
  } else {
    return chart.getIn([
      'properties',
      `yAxis${fieldLeftOrRight}Labels`,
      'format',
    ]);
  }
}

export function layerName(chart, field) {
  const xAxis = getXAxis(chart);

  return `id-${chart.get('id')}-name-${cleanAxisName(
    xAxis.get('name')
  )}-field-${cleanAxisName(field)}`;
}

export function isDatasetYAxisSelected(yAxis) {
  return yAxis.getIn(['overrides', 'selected']);
}

const FIRST_ASCII_CHARACTER = String.fromCharCode(0);

export function yAxesSorter(yAxis) {
  let sortPrefix = '';

  // FIXME: this is a hack to get selected fields to show first.
  if (isDatasetYAxisSelected(yAxis)) {
    sortPrefix = FIRST_ASCII_CHARACTER;
  }

  const possiblyOverridenYAxisName =
    yAxis.getIn(['overrides', 'name']) || yAxis.get('name');

  return `${sortPrefix}${possiblyOverridenYAxisName.toLowerCase()}`;
}

export function cleanAxisName(axisName) {
  return `${axisName
    // NOTE: https://vega.github.io/vega-lite/docs/field.html
    .replace(/\W/g, ' ')
    .split(' ')
    .join('_')}`;
}

export function annotationMarkLayerNamePrefix(markName) {
  // NOTE: this should not be `Y_FIELD_MARK_LAYER_PREFIX`.
  return canonicalizeLayerName(`y-field-${markName}`);
}

export function annotationMarkLayerName(markName, yAxis) {
  return canonicalizeLayerName(
    `${annotationMarkLayerNamePrefix(markName)}-${cleanAxisName(
      yAxis.get('name')
    )}`
  );
}

export function makeSpec(story, options) {
  const chart = story.get('chart');
  const annotationSet = story.get('annotationSet');
  const dataset = chart.getIn(['datasets', 0]);
  const annotations = annotationSet.get('annotations');

  options = options || {};

  const chartOptions = options.chart || {};
  const annotationSetOptions = options.annotationSet || {};

  if (isNullOrUndefined(annotationSetOptions.hideAnnotations)) {
    annotationSetOptions.hideAnnotations = Set();
  }

  if (
    isBlank(chart.getIn(['datasets', 0, 'metadata'])) ||
    chart.getIn(['datasets', 0, 'metadata', 'recordsCount']) === 0
  ) {
    return {};
  }

  const xAxis = getXAxis(chart);
  const cleanXAxisName = cleanAxisName(xAxis.get('name'));

  const selectedYAxes = getSelectedYAxes(chart);

  const fieldOrderByName = selectedYAxes.reduce(
    (m, yAxis) =>
      m.set(cleanAxisName(yAxis.get('name')), yAxis.get('fieldOrder')),
    Map()
  );

  const narrowedDateRange = chart.get('narrowedDateRange');
  const narrowedDateRangeStart = narrowedDateRange.get(0);
  const narrowedDateRangeEnd = narrowedDateRange.get(1);

  function isTimestampOutsideNarrowedDateRange(timestamp) {
    if (narrowedDateRangeStart && timestamp < narrowedDateRangeStart) {
      return true;
    }

    if (narrowedDateRangeEnd && timestamp > narrowedDateRangeEnd) {
      return true;
    }

    return false;
  }

  function filterOutRowsOutsideNarrowOrWithNoSelectedYAxes(row) {
    const x = row.get(xAxis.get('index'));

    if (isTimestampOutsideNarrowedDateRange(x)) {
      return true;
    }

    const rowHasCellsFromSelectedYAxes = selectedYAxes.some(
      (selectedYAxis) => row.get(selectedYAxis.get('index')) !== null
    );

    if (!rowHasCellsFromSelectedYAxes) {
      return true;
    }

    return false;
  }

  const cleanFieldNamesByIndex = selectedYAxes.reduce(
    (m, yAxis) => m.set(yAxis.get('index'), cleanAxisName(yAxis.get('name'))),
    Map()
  );

  const annotationIdsByTimestampAndYFieldColumnIndex = annotations
    .filterNot(
      (annotation) =>
        annotationSetOptions.hideAnnotations.has(annotation.get('id')) ||
        isTimestampOutsideNarrowedDateRange(new Date(annotation.get('date')))
    )
    .groupBy((annotation) => {
      return annotation.get('date');
    })
    .reduce((m, annotations, annotationDate) => {
      const annotation = annotations.first();

      const yFieldColumnIndex = isNumber(
        annotation.get('targetChartDatasetColumn')
      )
        ? annotation.get('targetChartDatasetColumn')
        : annotationSet.get('targetChartDatasetColumn');

      if (!isNumber(yFieldColumnIndex)) {
        console.error(
          `Target column '${yFieldColumnIndex}' for ${annotation.get(
            'id'
          )} is invalid`
        );
      }

      // TODO: it would be easy to map `correlatedAnnotations` above to get a
      // map with all y field column index keys.
      return m.set(
        annotationDate,
        Map([
          [
            yFieldColumnIndex,
            annotations.map((annotation) => annotation.get('id')).toSet(),
          ],
        ])
      );
    }, Map());

  const filteredRows = dataset
    .get('data')
    .filterNot(filterOutRowsOutsideNarrowOrWithNoSelectedYAxes);

  const emptyRow = List(Array(filteredRows.first().size).fill(null));

  const filteredAnnotationVirtualRows = annotations
    .map((annotation) =>
      emptyRow.set(xAxis.get('index'), annotation.get('date'))
    )
    .filterNot((row) =>
      isTimestampOutsideNarrowedDateRange(row.get(xAxis.get('index')))
    )
    .sortBy((row) => row.get(xAxis.get('index')));

  const filteredRowsWithVirtualAnnotationRows = mergeSortedLists(
    filteredRows,
    filteredAnnotationVirtualRows,
    (row) => row.get(xAxis.get('index')),
    (filteredRows, _annotationVirtualRows) => filteredRows
  );

  const timestampedValuesByYFieldName = filteredRows
    .flatMap((row) =>
      selectedYAxes.map((yAxis) => ({
        yFieldName: yAxis.get('name'),
        timestamp: row.get(xAxis.get('index')),
        value: row.get(yAxis.get('index')),
      }))
    )
    .filter(({ value }) => value !== null)
    .groupBy(({ yFieldName }) => cleanAxisName(yFieldName))
    .reduce(
      (m, group, yFieldName) =>
        m.set(
          yFieldName,
          group.map(({ timestamp, value }) => [
            new Date(timestamp).getTime(),
            value,
          ])
        ),
      Map()
    );

  const latestPossiblyNarrowedTimestampByYField = selectedYAxes.reduce(
    (m, yAxis) => {
      // REFACTOR: use `reduceRight`? Or move this to the data model, set in the
      // backend.
      const latestYFieldRow = filteredRows
        .filter((row) => isPresent(row.get(yAxis.get('index'))))
        .last();

      if (latestYFieldRow === null || latestYFieldRow === undefined) {
        return m;
      }

      const latestYFieldTimestamp = latestYFieldRow.get(xAxis.get('index'));

      return m.set(cleanAxisName(yAxis.get('name')), latestYFieldTimestamp);
    },
    Map()
  );

  const annotationsById = annotationSet
    .get('annotations')
    .reduce((m, annotation) => m.set(annotation.get('id'), annotation), Map());

  const yFieldValues = filteredRowsWithVirtualAnnotationRows.flatMap((row) => {
    const timestamp = row.get(xAxis.get('index'));
    const yFieldDataOnTimestamp = Map([[cleanXAxisName, timestamp]]);
    const annotationIdsByYFieldColumnIndex =
      annotationIdsByTimestampAndYFieldColumnIndex.get(timestamp);

    return selectedYAxes.map((yAxis) => {
      const annotationIds = annotationIdsByYFieldColumnIndex
        ? annotationIdsByYFieldColumnIndex.get(yAxis.get('index'))
        : null;

      const annotations = annotationIds
        ? annotationIds
            .map((annotationId) => annotationsById.get(annotationId))
            .map((annotation) =>
              annotation.set(
                'derivedTitle',
                breakString(
                  Annotation.derivedTitle(annotation),
                  annotation.getIn(['style', 'length'])
                )
              )
            )
        : null;

      const cleanYAxisName = cleanFieldNamesByIndex.get(yAxis.get('index'));

      const possiblyOverridenYAxisName =
        yAxis.getIn(['overrides', 'name']) || yAxis.get('name');

      let yFieldLabel = null;

      if (
        timestamp ===
        latestPossiblyNarrowedTimestampByYField.get(cleanYAxisName)
      ) {
        yFieldLabel = Map([
          [
            'label',
            breakString(
              possiblyOverridenYAxisName,
              yAxis.getIn(['style', 'labelLength'])
            ),
          ],
          ['yAxis', yAxis],
        ]);
      }

      return yFieldDataOnTimestamp
        .set('key', cleanYAxisName)
        .set('originalValue', row.get(yAxis.get('index')))
        .set('value', row.get(yAxis.get('index')))
        .set('order', fieldOrderByName.get(cleanYAxisName))
        .set('annotations', annotations)
        .set('fieldLabel', yFieldLabel);
    });
  });

  const leftAxisVariables = Set(
    chart.getIn(['dualAxisChartProperties', 'leftAxisVariables'])
  );
  const rightAxisVariables = Set(
    chart.getIn(['dualAxisChartProperties', 'rightAxisVariables'])
  );

  // NOTE: this uses the Y-fields' metadata for minimum and maximum, and not the
  // values within a possibly narrowed range.
  const yAxesVariableBounds = selectedYAxes
    .map((yAxis) => [yAxis.get('variable'), yAxis.get('min'), yAxis.get('max')])
    .groupBy(([variable, _min, _max]) => variable)
    .reduce((m, yAxesBounds, variable) => {
      const mins = yAxesBounds.map(([_variable, min, _max]) => min);
      const maxes = yAxesBounds.map(([_variable, _min, max]) => max);
      return m.set(variable, [Math.min(...mins), Math.max(...maxes)]);
    }, Map());

  const axisBoundsReducer = ([minSoFar, maxSoFar], variable) => {
    const bounds = yAxesVariableBounds.get(variable);
    if (isNullOrUndefined(bounds)) {
      return [minSoFar, maxSoFar];
    }
    const [min, max] = bounds;
    return [Math.min(min, minSoFar), Math.max(max, maxSoFar)];
  };

  const emptyBounds = [+Infinity, -Infinity];

  const leftAxisBounds = leftAxisVariables.reduce(
    axisBoundsReducer,
    emptyBounds
  );
  const rightAxisBounds = rightAxisVariables.reduce(
    axisBoundsReducer,
    emptyBounds
  );

  const leftAndRightAxisBounds = [
    { axis: 'Left', bounds: leftAxisBounds },
    { axis: 'Right', bounds: rightAxisBounds },
  ].filter(({ bounds: [min, max] }) => min !== +Infinity && max !== -Infinity);

  const yScaleResolution =
    leftAndRightAxisBounds.length > 1 ? 'independent' : 'shared';

  const isNarrowed =
    narrowedDateRangeStart !== null && narrowedDateRangeEnd !== null;

  const timeSeriesPropertiesMark = chart.getIn([
    'timeSeriesProperties',
    'mark',
  ]);

  const shouldStack =
    chart.getIn(['properties', 'stackFields']) &&
    timeSeriesPropertiesMark !== 'line' &&
    leftAndRightAxisBounds.length === 1;

  // TODO: the interpolated value needs to take into consideration non-automatic
  // chart "x-axis data resolution" settings, otherwise values won't match the
  // marks.
  function interpolateFromSurroundings(epochMs, yAxisName) {
    const yFieldTimestampedValues =
      timestampedValuesByYFieldName.get(yAxisName);

    const beforeAndAfter = getElementsAround(
      yFieldTimestampedValues,
      epochMs,
      ([timestamp, _value]) => timestamp
    );

    if (beforeAndAfter === null) {
      return 0;
    }

    const [before, after] = beforeAndAfter;

    if (before === null || after === null) {
      return 0;
    }

    const interpolatedValue = interpolate(
      before[0],
      after[0],
      before[1],
      after[1],
      epochMs
    );

    return isNumber(interpolatedValue) ? interpolatedValue : 0;
  }

  const interpolateFromSurroudingsFunctionName =
    makeInterpolateFromSurroudingsFunctionName(story);

  if (typeof window !== 'undefined') {
    window[interpolateFromSurroudingsFunctionName] =
      interpolateFromSurroundings;
  }

  vega.expressionFunction(
    interpolateFromSurroudingsFunctionName,
    interpolateFromSurroundings
  );

  const spec = {
    // NOTE: setting "name" here causes the `view` object not have a
    // `view.scale('x')` for whatever reason.
    description: chart.get('name'),
    data: {
      values: yFieldValues.toJS(),
    },
    transform: [
      shouldStack
        ? {
            stack: 'value',
            groupby: [cleanXAxisName],
            offset: 'zero',
            as: 'stacked_value',
            sort: [{ field: 'order', order: 'ascending' }],
          }
        : null,
    ].filter((x) => x !== null),
    resolve: { scale: { y: yScaleResolution } },
    encoding: {
      x: {
        field: cleanXAxisName,
        type: timeSeriesPropertiesMark === 'bar' ? 'ordinal' : 'temporal',
        ...(chart.getIn(['timeSeriesProperties', 'timeUnit'])
          ? {
              timeUnit: chart.getIn(['timeSeriesProperties', 'timeUnit']),
            }
          : null),
        title: chart.getIn(['properties', 'xAxisTitle', 'show'])
          ? breakString(
              chart.getIn(['properties', 'xAxisTitle', 'text']),
              chart.getIn(['properties', 'xAxisTitle', 'length'])
            )
          : null,
        axis: {
          domain: true,
          domainColor: DOMAIN_COLOR,
          grid: false,
          // NOTE: `labelBound` is also removing extremity labels which are
          // present in the scale domain in some charts.
          //
          // labelBound: true,
          labelAngle: chart.getIn(['properties', 'xAxisLabels', 'angle']),
          ...(chart.getIn(['properties', 'xAxisLabels', 'angle']) === 0
            ? {
                labelAlign: 'center',
              }
            : null),
          labelOverlap: 'greedy',
          labelSeparation: chart.getIn([
            'properties',
            'xAxisLabels',
            'separation',
          ]),
          ticks: true,
          labelFontSize: chartOptions.axisFontSize || AXIS_FONT_SIZE,
          labels: true,
          labelColor: chart.getIn(['properties', 'xAxisLabels', 'color']),
          formatType: 'time',
          format: chart.getIn(['properties', 'xAxisLabels', 'format']),
          ...(chart.getIn(['properties', 'xAxisTitle', 'show']) && {
            titleFontWeight: 'normal',
            titleFontSize: chart.getIn([
              'properties',
              'xAxisTitle',
              'fontSize',
            ]),
            titlePadding: chart.getIn(['properties', 'xAxisTitle', 'padding']),
            titleColor: chart.getIn(['properties', 'xAxisTitle', 'color']),
          }),
        },
        ...(timeSeriesPropertiesMark === 'bar'
          ? null
          : {
              scale: {
                // NOTE: only make it "nice" if not narrowed.
                nice: !isNarrowed,
                // NOTE: `nice=true` will add some horizontal padding to the x-axis so
                // that it starts and ends on a tick. Removing it will cause the line
                // to start at x=0 and end at the max x.
                //
                // NOTE(CTX-182): the padding added to the right with `nice=true`
                // might be undesirable.
                //
                // NOTE: A fixed `nice=year` will look nice for wide domain ranges but
                // will screw things up in narrower ones. For example when narrowing
                // date ranges.
                type: 'utc',
              },
            }),
      },
    },
    // NOTE: one layer per axis side. This makes sure that the dual-axis
    // domains are correct.
    layer: leftAndRightAxisBounds.map(({ axis, bounds }) => {
      return {
        layer: selectedYAxes
          .filter((selectedYAxis) =>
            axis === 'Left'
              ? leftAxisVariables.has(selectedYAxis.get('variable'))
              : axis === 'Right'
              ? rightAxisVariables.has(selectedYAxis.get('variable'))
              : false
          )
          // NOTE: this makes it possible to re-order area chart y-field
          // z-indices.
          .sortBy((yAxis) => yAxis.get('fieldOrder'))
          .map((selectedYAxis) => {
            const cleanYAxisName = cleanAxisName(selectedYAxis.get('name'));

            const normalizedBoundDifference =
              (PERCENTAGE_Y_AXIS_PADDING_VERTICAL / 100) *
              (bounds[1] - bounds[0]);

            const pointMarkProperties = {
              filled: false,
              size:
                selectedYAxis.getIn(['style', 'strokeWidth']) *
                POINT_MARK_STROKE_WIDTH_SIZE_MULTIPLIER,
              fill: 'white',
              stroke: selectedYAxis.getIn(['style', 'strokeColor']),
            };

            return {
              transform: [
                {
                  filter: `datum['key'] === '${cleanYAxisName}'`,
                },
              ],
              encoding: {
                ...(shouldStack
                  ? {
                      y2: { field: 'stacked_value_end' },
                    }
                  : null),

                ...(timeSeriesPropertiesMark === 'bar'
                  ? shouldStack
                    ? null
                    : {
                        // NOTE: having the xOffset field be "order" and not
                        // "key" makes it possible to re-order bars in a grouped
                        // bar chart.
                        xOffset: { field: 'order' },
                      }
                  : null),
                y: {
                  field: shouldStack ? 'stacked_value' : 'value',
                  type: 'quantitative',
                  title: chart.getIn([
                    'properties',
                    `yAxis${axis}Title`,
                    'show',
                  ])
                    ? breakString(
                        chart.getIn([
                          'properties',
                          `yAxis${axis}Title`,
                          'text',
                        ]),
                        chart.getIn([
                          'properties',
                          `yAxis${axis}Title`,
                          'length',
                        ])
                      )
                    : null,
                  axis: {
                    ticks: true,
                    tickExtra: true,
                    labels: true,
                    tickCount: 5,
                    ...(leftAndRightAxisBounds.length === 1 && axis === 'Right'
                      ? { orient: 'right' }
                      : null),
                    grid:
                      yScaleResolution === 'independent' && axis === 'Right'
                        ? false
                        : true,
                    gridOpacity: 0.75,
                    domain: true,
                    domainColor: DOMAIN_COLOR,
                    labelColor: chart.getIn([
                      'properties',
                      `yAxis${axis}Labels`,
                      'color',
                    ]),
                    labelFontSize: chartOptions.axisFontSize || AXIS_FONT_SIZE,
                    ...(isPresent(
                      chart.getIn([
                        'properties',
                        `yAxis${axis}Labels`,
                        'format',
                      ])
                    ) && {
                      format: chart.getIn([
                        'properties',
                        `yAxis${axis}Labels`,
                        'format',
                      ]),
                      ...(chart
                        .getIn(['properties', `yAxis${axis}Labels`, 'format'])
                        .startsWith('$')
                        ? {
                            formatType: 'currencyFormatter',
                          }
                        : null),
                    }),
                    ...(chart.getIn([
                      'properties',
                      `yAxis${axis}Title`,
                      'show',
                    ]) && {
                      titleFontWeight: 'normal',
                      titleFontSize: chart.getIn([
                        'properties',
                        `yAxis${axis}Title`,
                        'fontSize',
                      ]),
                      titlePadding: chart.getIn([
                        'properties',
                        `yAxis${axis}Title`,
                        'padding',
                      ]),
                      titleColor: chart.getIn([
                        'properties',
                        `yAxis${axis}Title`,
                        'color',
                      ]),
                    }),
                  },
                  scale: {
                    ...(chart.getIn(['properties', 'isScaleZeroBased'])
                      ? null
                      : isNarrowed
                      ? null
                      : {
                          domainMin: Math.floor(
                            bounds[0] - normalizedBoundDifference
                          ),
                        }),
                    ...(shouldStack
                      ? null
                      : isNarrowed
                      ? null
                      : {
                          domainMax: Math.ceil(
                            bounds[1] + normalizedBoundDifference
                          ),
                        }),
                    zero: chart.getIn(['properties', 'isScaleZeroBased']),
                    nice: true,
                  },
                },
              },
              layer: [
                // Y-axis mark layer.
                {
                  transform: [
                    {
                      // NOTE: this prevents gaps in the line mark.
                      filter: "datum['value'] !== null",
                    },
                  ],
                  name: canonicalizeLayerName(
                    `${Y_FIELD_MARK_LAYER_PREFIX}-${layerName(
                      chart,
                      selectedYAxis.get('name')
                    )}`
                  ),
                  description: chart.get('name'),
                  mark: {
                    type: timeSeriesPropertiesMark,
                    clip: true,
                    ...(selectedYAxis.getIn(['style', 'showPoints'])
                      ? {
                          point: pointMarkProperties,
                        }
                      : null),
                    // NOTE: hack to attach data directly to the fucking SVG
                    // element. Why does this have to be so hard?
                    //
                    // This is used by the LineGlowRenderer.
                    description: JSON.stringify({
                      glow: selectedYAxis.getIn(['style', 'glow']),
                    }),
                    stroke: selectedYAxis.getIn(['style', 'strokeColor']),
                    strokeWidth: selectedYAxis.getIn(['style', 'strokeWidth']),
                    strokeOpacity: selectedYAxis.getIn([
                      'style',
                      'strokeOpacity',
                    ]),
                    ...(timeSeriesPropertiesMark === 'line'
                      ? null
                      : {
                          fill: selectedYAxis.getIn(['style', 'fillColor']),
                          fillOpacity: selectedYAxis.getIn([
                            'style',
                            'fillOpacity',
                          ]),
                        }),
                    ...(timeSeriesPropertiesMark === 'bar'
                      ? {
                          size: chart.getIn(['barChartProperties', 'barWidth']),
                        }
                      : null),
                  },
                },
                // Parent layer for Y-field labels and annotations layers.
                {
                  transform: [
                    {
                      calculate: `datum['annotations'] !== null && datum['value'] === null ? ${interpolateFromSurroudingsFunctionName}(datum['${cleanXAxisName}'], '${cleanYAxisName}') : datum['value']`,
                      // NOTE: even when stacked, 'value' here places the
                      // annotation target on the correct spot for some reason
                      // that is probably obvious and I'm just not realizing
                      // right now.
                      as: 'value',
                    },
                  ],
                  encoding: {
                    y: {
                      field: shouldStack ? 'stacked_value_end' : 'value',
                      type: 'quantitative',
                      axis: {
                        ticks: false,
                        labels: false,
                        grid: false,
                        domain: false,
                      },
                    },
                  },
                  layer: [
                    // Y-field labels layer.
                    {
                      name: canonicalizeLayerName(
                        `${Y_FIELD_LABEL_LAYER_PREFIX}-${layerName(
                          chart,
                          selectedYAxis.get('name')
                        )}`
                      ),
                      transform: [
                        {
                          filter: "datum['fieldLabel'] !== null",
                        },
                      ],
                      layer: [
                        {
                          mark: {
                            type: 'text',
                            align: selectedYAxis.getIn([
                              'style',
                              'labelTextAlign',
                            ]),
                            dx:
                              MINIMUM_DISTANCE_BETWEEN_Y_FIELD_LABEL_AND_MARK +
                              (timeSeriesPropertiesMark === 'bar'
                                ? chart.getIn([
                                    'barChartProperties',
                                    'barWidth',
                                  ])
                                : 0),
                            fontWeight: 'bold',
                            // NOTE: this is so that the SVG area isn't expanded
                            // due to labels rendering.
                            fontSize: 1,
                            lineHeight: selectedYAxis.getIn([
                              'style',
                              'labelFontSize',
                            ]),
                          },
                          encoding: {
                            color: {
                              value: selectedYAxis.getIn([
                                'style',
                                'strokeColor',
                              ]),
                            },
                            text: {
                              field: 'fieldLabel.label',
                              type: 'nominal',
                            },
                          },
                        },
                      ],
                    },
                    // Annotations layer.
                    //
                    // NOTE: might be able to render multiple annotations by
                    // using a flatten transform:
                    // https://vega.github.io/vega-lite/docs/flatten.html
                    annotationSetOptions.hideAllAnnotations
                      ? null
                      : {
                          description: annotationSet.get('name'),
                          transform: [
                            {
                              filter: "datum['annotations'] !== null",
                            },
                          ],
                          // NOTE: `layer.name` influences the element class name, which ends up
                          // looking like `.${undescorized_layer_name}_marks`.
                          layer: [
                            {
                              name: annotationMarkLayerName(
                                ANNOTATION_POINTERS_LAYER_PREFIX,
                                selectedYAxis
                              ),
                              description:
                                'Pointers from annotations to when events happened.',
                              mark: {
                                type: 'point',
                                shape: 'triangle-down',
                              },
                              encoding: {
                                size: {
                                  value: {
                                    expr: 'datum.annotations[0].style.showArrow === false ? 0 : 30',
                                  },
                                },
                              },
                            },
                            {
                              name: annotationMarkLayerName(
                                ANNOTATION_POINTER_TARGETS_LAYER_PREFIX,
                                selectedYAxis
                              ),
                              description:
                                'Points on mark related to the annotation',
                              mark: {
                                type: 'circle',
                                stroke: '#aaaaaa',
                                strokeWidth: 1,
                              },
                              encoding: {
                                size: {
                                  value: {
                                    expr: "datum['originalValue'] === null ? 20 : 0",
                                  },
                                },
                                fill: {
                                  value: {
                                    expr: 'datum.annotations[0].style.color',
                                  },
                                },
                              },
                            },
                            {
                              name: annotationMarkLayerName(
                                ANNOTATION_TEXTS_LAYER_PREFIX,
                                selectedYAxis
                              ),
                              description: 'Annotation texts',
                              mark: {
                                type: 'text',
                                // NOTE: this is a hack so that the rendering of the initial chart by
                                // Vega doesn't offset the container width with super long text lines.
                                fontSize: 1,
                                lineHeight: {
                                  expr: 'datum.annotations[0].style.fontSize',
                                },
                                // NOTE: this affects the alignment of the resulting rect background
                                // BBox positioning. It will be offset to the right, as if the text
                                // was left-aligned.
                                align: {
                                  expr: 'datum.annotations[0].style.textAlign',
                                },
                              },
                              encoding: {
                                text: {
                                  field: 'annotations[0].derivedTitle',
                                  type: 'nominal',
                                },
                                color: {
                                  field: 'annotations[0].style.textColor',
                                  // NOTE: this disables the color legend element.
                                  // https://vega.github.io/vega-lite/docs/scale.html#disable
                                  scale: null,
                                },
                              },
                            },
                          ],
                        },
                  ].filter((x) => x !== null),
                },
              ],
            };
          })
          .toJS(),
      };
    }),
  };

  return spec;
}
